import { useState } from "react";
import { Switch } from "@headlessui/react";
import { LBSubscriptionStatus } from "@/services/LBSubscriptionService";

type LBSubscriptionToggleProps = {
    subscriptionStatus: LBSubscriptionStatus;
    onToggle: (subscribed: boolean, reason?: string) => Promise<void>;
    isLoading: boolean;
};

export const LBSubscriptionToggle: React.FC<LBSubscriptionToggleProps> = ({
    subscriptionStatus,
    onToggle,
    isLoading,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reason, setReason] = useState("");
    const [pendingValue, setPendingValue] = useState<boolean | null>(null);

    const handleToggleClick = (newValue: boolean) => {
        setPendingValue(newValue);
        setIsModalOpen(true);
    };

    const handleConfirm = async () => {
        if (pendingValue !== null) {
            await onToggle(pendingValue, reason);
            setIsModalOpen(false);
            setReason("");
            setPendingValue(null);
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setReason("");
        setPendingValue(null);
    };

    return (
        <>
            <div>
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        <h3 className="text-base font-medium text-gray-900">Email subscription status</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {subscriptionStatus.lbEmailSubscribed
                                ? "User is subscribed to leaderboard emails"
                                : "User is unsubscribed from leaderboard emails"}
                        </p>
                        {subscriptionStatus.lastModified && (
                            <p className="mt-1 text-xs text-gray-400">
                                Last modified by {subscriptionStatus.lastModifiedBy} on{" "}
                                {new Date(subscriptionStatus.lastModified).toLocaleString()}
                            </p>
                        )}
                    </div>
                    <Switch
                        checked={subscriptionStatus.lbEmailSubscribed}
                        onChange={handleToggleClick}
                        disabled={isLoading}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                            subscriptionStatus.lbEmailSubscribed ? "bg-primary" : "bg-gray-200"
                        }`}
                    >
                        <span className="sr-only">Toggle subscription</span>
                        <span
                            className="inline-block h-4 w-4 rounded-full bg-white transition-transform duration-200 ease-in-out"
                            style={{
                                transform: subscriptionStatus.lbEmailSubscribed
                                    ? "translateX(24px)"
                                    : "translateX(4px)",
                            }}
                        />
                    </Switch>
                </div>
            </div>

            {/* Confirmation Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                            onClick={handleCancel}
                        ></div>

                        {/* This element is to trick the browser into centering the modal contents. */}
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                            &#8203;
                        </span>

                        <div className="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 z-10">
                            <div>
                                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                                    {pendingValue ? "Subscribe user" : "Unsubscribe user"}
                                </h3>
                                <p className="text-sm text-gray-500 mb-4">
                                    Are you sure you want to {pendingValue ? "subscribe" : "unsubscribe"}{" "}
                                    <span className="font-medium">{subscriptionStatus.username}</span>{" "}
                                    {pendingValue ? "to" : "from"} leaderboard emails?
                                </p>
                                <div>
                                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-2">
                                        Reason (optional)
                                    </label>
                                    <textarea
                                        id="reason"
                                        rows={3}
                                        className="block w-full rounded-md border-gray-300 shadow-xs focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                        placeholder="Enter reason for this change..."
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                <button
                                    type="button"
                                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={handleConfirm}
                                    disabled={isLoading}
                                >
                                    Confirm
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
